/**
 * Activity Trends chart — a dependency-free, two-series bar chart for the
 * monthly time-series exposed by the analytics endpoints (`activityTrend`).
 *
 * Two stacked rows share one month axis:
 *   - Monthly Match Velocity  → completed (reported) matches per month.
 *   - Rolling Active Players   → distinct players who played that month.
 *
 * Each series is normalised to its OWN max so both remain legible despite very
 * different magnitudes — this is a shape-over-time view, not a shared-axis
 * comparison (the raw counts are printed above every bar to keep it honest).
 *
 * Styling follows the two-skin model: night markup is base + `dark:`, and
 * `daylight:` classes are APPENDED for the light skin (never edited in).
 */

import type { ActivityTrendPoint } from "shared";
import { t } from "../i18n/config";

const esc = (value: string): string => {
  const div = document.createElement("div");
  div.textContent = value ?? "";
  return div.innerHTML;
};

/** "2026-06" → "Jun" (locale short month). Falls back to the raw key. */
const monthLabel = (key: string): string => {
  const [year, month] = key.split("-").map(Number);
  if (!year || !month) return key;
  return new Date(year, month - 1, 1).toLocaleString(undefined, { month: "short" });
};

/** Whether a "YYYY-MM" key is January — used to print a year tick. */
const isJanuary = (key: string): boolean => key.endsWith("-01");

/** One series of vertical bars normalised to its own max. */
function barRow(
  points: ActivityTrendPoint[],
  value: (p: ActivityTrendPoint) => number,
  barClass: string,
): string {
  const max = Math.max(1, ...points.map(value));
  return points
    .map((p) => {
      const count = value(p);
      const heightPct = Math.round((count / max) * 100);
      return `
        <div class="flex flex-col items-center justify-end gap-1.5 flex-1 min-w-[1.5rem]">
          <span class="text-[0.625rem] font-mono text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">${count}</span>
          <div class="w-full flex items-end" style="height: 72px">
            <div class="w-full rounded-t ${barClass} transition-all" style="height: ${Math.max(heightPct, count > 0 ? 6 : 0)}%" title="${count}"></div>
          </div>
        </div>`;
    })
    .join("");
}

/** Shared month-label axis printed once beneath the two series. */
function axisRow(points: ActivityTrendPoint[]): string {
  return points
    .map(
      (p) => `
      <div class="flex flex-col items-center flex-1 min-w-[1.5rem]">
        <span class="text-[0.625rem] text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">${esc(monthLabel(p.month))}</span>
        ${
          isJanuary(p.month)
            ? `<span class="text-[0.5rem] font-bold text-slate-400 dark:text-slate-500 daylight:text-day-ink-muted">${esc(p.month.slice(0, 4))}</span>`
            : ""
        }
      </div>`,
    )
    .join("");
}

function legendDot(label: string, dotClass: string): string {
  return `
    <span class="inline-flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">
      <span class="size-2.5 rounded-full ${dotClass}"></span>${esc(label)}
    </span>`;
}

/**
 * Render the Activity Trends card. `points` is expected to be the contiguous
 * 6–12 month window from the API (empty months included). When every month is
 * empty the card degrades to a guidance state rather than a flat row of zeros.
 */
export function renderActivityTrendCard(points: ActivityTrendPoint[]): string {
  const heading = `
    <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
      <div class="flex items-center gap-2">
        <span class="material-symbols-outlined text-primary daylight:text-day-ink">trending_up</span>
        <h3 class="font-bold text-slate-900 dark:text-white daylight:text-day-ink">${t("insights.activity.title")}</h3>
      </div>
      <div class="flex items-center gap-3">
        ${legendDot(t("insights.activity.legendMatches"), "bg-primary")}
        ${legendDot(t("insights.activity.legendActivePlayers"), "bg-success daylight:bg-day-success")}
      </div>
    </div>
  `;

  const totalActivity = points.reduce((sum, p) => sum + p.completedMatches + p.activePlayers, 0);

  if (points.length === 0 || totalActivity === 0) {
    return `
      <div class="card">
        ${heading}
        <p class="text-sm text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted py-6 text-center">
          ${t("insights.activity.empty", { count: points.length || 12 })}
        </p>
      </div>
    `;
  }

  return `
    <div class="card">
      ${heading}
      <div class="overflow-x-auto">
        <div class="min-w-[32rem] space-y-4">
          <div>
            <p class="text-xs font-medium text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted mb-1">${t("insights.activity.matchVelocity")}</p>
            <div class="flex items-end gap-2">${barRow(points, (p) => p.completedMatches, "bg-primary daylight:shadow-day-ball")}</div>
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted mb-1">${t("insights.activity.activePlayers")}</p>
            <div class="flex items-end gap-2">${barRow(points, (p) => p.activePlayers, "bg-success daylight:bg-day-success")}</div>
          </div>
          <div class="flex gap-2 pt-1 border-t border-slate-200 dark:border-slate-700 daylight:border-day-ink/10">${axisRow(points)}</div>
        </div>
      </div>
    </div>
  `;
}
